import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { MessageProcessor, type A2uiClientAction, type ComponentApi, type SurfaceModel } from '@a2ui/web_core/v0_9';
import '@a2ui/lit/v0_9'; // registers <a2ui-surface>
import { api, HttpError, isRetryable } from '../api';
import { onSpeaking, speak } from '../tts';
import { CATALOG_ID, SURFACE_ID, exerciseCatalog } from '../a2ui/catalog';
import { adoptAppStyles } from '../a2ui/adopt-styles';
import { go, setLeaveGuard } from '../router';
import type { AttemptInput, Exam, ExamTask, LearnStatus, PackTask, PastExam, ReviewQueueEntry } from '../types';
import { cardMs, cardShown } from '../card-time';
import { answerHidden, COMPONENT, givenText, listenAudioText, noteMiss as noteMissIn, showAnswerOn, withoutSaying, type MissNote } from './lesson-view';
import { currentLang, errorText, t } from '../i18n';
import { backGlyph, iso, targetList, uiDir } from '../bidi-html';
import { missedRow } from './missed-row';
import { makeClientId } from '../drafts';
import { playButton, playState } from './wait-bits';
import { storyWidths } from './wait-steps';
import { inkDelays } from './wait-lines';
import { revealBar } from './reveal-bar';

/** Lines on the exam wait's page: two short paragraphs. */
const STORY_LINES = 8;

/** An answer the server has not confirmed (exam answers post to each item's own moment). */
type UnsavedAttempt = { momentId: string; a: AttemptInput & { attemptId: string } };
/** Saved; worth sending again (connection, 5xx, 408, 429); or refused for good (any other 4xx). */
type SendResult = 'saved' | 'retry' | 'refused';

/**
 * Scenario exam (text v2): a Flash-written story reusing met words (the
 * shakiest first, a couple of forged ones), then hard-gated questions on
 * those words — meaning, spelling, plus story-grounded missions (hear a
 * line, pick a reply, recall a phrase). Every answer posts to the item's
 * home moment — the same stats and Leitner boxes as practice. Pass mark: 80%.
 *
 * The server stores each finished exam's score from the first answer to
 * each question (v1 ticket 10); the intro and the score screen list them.
 *
 * A missed word's easier card (ticket 04) waits until the story's questions
 * are done: they come after the last question, before the score, and never
 * change it. One left unanswered leads the next review.
 */
@customElement('exam-view')
export class ExamView extends LitElement {
  @state() private exam?: Exam;
  @state() private phase: 'intro' | 'story' | 'test' | 'easier' | 'done' = 'intro';
  /** Easier cards for the words missed in this exam, asked after its questions. */
  @state() private easier: ReviewQueueEntry[] = [];
  @state() private i = 0;
  @state() private answered = false;
  /** Ticket 32: a miss left the answer off screen, so the bar offers Show me instead of Next. */
  @state() private showMe = false;
  @state() private recordedFor = -1;
  @state() private correct = 0;
  @state() private missed: MissNote[] = [];
  @state() private loading = false;
  @state() private error = '';
  @state() private readiness?: LearnStatus;
  @state() private checkingReadiness = true;
  @state() private readinessUnavailable = false;
  @state() private refusedWithEligibleStatus = false;
  /** Newest refreshReadiness call wins: a slow earlier read never overwrites a later one. */
  private readinessSeq = 0;
  @state() private saving = false;
  /** Answers whose save failed: kept for Retry, across questions, until they land. */
  @state() private unsaved: UnsavedAttempt[] = [];
  @state() private retrying = false;
  /** Answers the server refused outright (their moment was deleted, …): only counted until dismissed. */
  @state() private lost = 0;
  /** Leaving (in the app, Back, closing the tab) with unsaved answers asks first: they live only here. */
  private leaveGuard = () => (this.unsaved.length ? t('lesson.leaveUnsaved') : null);
  @state() private speaking: string | null = null;
  /** Whether `speaking` is heard yet, or its voice still on its way (the play button prepares meanwhile). */
  @state() private heard = false;
  private unsubSpeaking: (() => void) | null = null;
  @state() private surface?: SurfaceModel<never>;
  /** Finished exams with their stored scores, newest first; undefined while loading. */
  @state() private past?: PastExam[];
  @state() private pastError = false;
  /** Answer saves still in flight: the score screen waits for them before it asks for the stored score. */
  private inflight = new Set<Promise<unknown>>();

  private processor: MessageProcessor<ComponentApi> | undefined;
  private actionHandler = (a: A2uiClientAction) => this.onAction(a);

  protected createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    setLeaveGuard(this.leaveGuard);
    this.processor = new MessageProcessor([exerciseCatalog], this.actionHandler);
    this.unsubSpeaking = onSpeaking((now, heard) => {
      this.speaking = now;
      this.heard = heard;
    });
    void this.refreshReadiness();
    void this.loadPast();
    this.processor.onSurfaceCreated((s) => {
      if (s.id === SURFACE_ID) this.surface = s as unknown as SurfaceModel<never>;
    });
    this.processor.onSurfaceDeleted((id) => {
      if (id === SURFACE_ID) this.surface = undefined;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    setLeaveGuard(null);
    this.unsubSpeaking?.();
    this.unsubSpeaking = null;
    this.processor?.model.dispose();
    this.processor = undefined;
  }

  protected updated(changed: PropertyValues) {
    if (changed.has('answered') && this.answered) revealBar(this);
    adoptAppStyles(this.querySelector('a2ui-surface')?.shadowRoot);
    const cur = this.phase === 'test' || this.phase === 'easier' ? this.current() : undefined;
    if (cur) cardShown(cur.momentId, cur.task.id);
  }

  render() {
    return html`
      <div class="screen">
        <div class="topbar">
          <button class="btn ghost icon" @click=${() => go('/')} aria-label=${t('common.homeAria')}>${backGlyph()}</button>
          <h1 style="font-size:17px">${t('exam.title')}</h1>
        </div>

        ${this.error ? html`<div class="card"><strong>${t('exam.buildError')}</strong><div class="muted small">${this.error}</div></div>` : nothing}

        ${this.phase === 'intro'
          ? html`${this.renderIntro()}
            ${this.loading ? this.renderStoryWait() : nothing}
            ${this.renderPast()}`
          : nothing}

        ${this.phase === 'story' && this.exam
          ? html`<div class="card">
              <h2>${t('exam.sceneTitle')}</h2>
              <div class="exam-story" dir=${uiDir(this.exam.story)}>${iso(this.exam.story)}</div>
              <button class="btn primary block" style="margin-top:14px;min-height:52px" @click=${this.beginTest}>
                ${t('exam.startQuestions', { n: this.exam.tasks.length })}
              </button>
            </div>`
          : nothing}

        ${(this.phase === 'test' || this.phase === 'easier') && this.exam
          ? html`
            ${this.phase === 'easier'
              ? html`<div class="muted small" style="margin-bottom:4px">${t('exam.easierCount', { i: this.i + 1, n: this.easier.length })}</div>
                  <p class="card-name" style="margin:0 0 4px;font-weight:600">${t('retreat.card')}</p>
                  <p class="muted small retreat-hint" style="margin:0 0 8px">${t('exam.easierIntro')}</p>`
              : html`<div class="muted small" style="margin-bottom:8px">${t('exam.question', { i: this.i + 1, n: this.exam.tasks.length })}</div>`}
            ${this.renderAudio()}
            <div class="surface">
              ${this.surface
                ? html`<a2ui-surface giveup .surface=${this.surface}></a2ui-surface>`
                : html`<div class="surface-empty"><span class="muted">${t('exam.loading')}</span></div>`}
            </div>
            ${this.renderUnsaved()}
            <div class="actionbar ${this.answered || this.showMe ? '' : 'waiting'}">
              ${this.showMe
                ? html`<button class="btn primary block" style="flex:1" @click=${this.showAnswer} ?disabled=${this.saving}>${t('ex.showMe')}</button>`
                : html`<button class="btn" style="flex:0 0 auto" @click=${this.skip} ?disabled=${this.saving}>${t('lesson.skip')}</button>
                  <button class="btn primary block" style="flex:1" @click=${this.next} ?disabled=${!this.answered || this.saving}>
                    ${this.isLast() ? t('exam.seeScore') : t('lesson.next')}
                  </button>`}
            </div>`
          : nothing}

        ${this.phase === 'done' && this.exam ? html`${this.renderDone()}${this.loading ? this.renderStoryWait() : nothing}${this.renderPast()}` : nothing}
      </div>
    `;
  }

  private renderAudio() {
    const audio = this.audioText();
    return audio
      ? html`<div class="card" style="display:flex;gap:10px;align-items:center;margin-bottom:8px">
          ${playButton(playState(this.speaking, this.heard, audio), this.playAudio)}
          <span class="muted small">${t('review.listenHint')}</span>
        </div>`
      : nothing;
  }

  /** Direct entry checks the same server-owned word boundary as discovery before offering Start. */
  private renderIntro() {
    if (this.checkingReadiness) return html`<div class="card exam-readiness" role="status">${t('exam.checkingReadiness')}</div>`;
    if (this.readinessUnavailable) return html`<div class="card exam-readiness" role="status">
      <div>${t('exam.readinessUnavailable')}</div>
      <button class="btn small" style="margin-top:10px" @click=${() => this.refreshReadiness()}>${t('common.retry')}</button>
    </div>`;
    if (this.refusedWithEligibleStatus) return html`<div class="card exam-readiness" role="status">
      <div>${t('exam.readinessChanged')}</div>
      <button class="btn small" style="margin-top:10px" @click=${() => this.refreshReadiness()}>${t('common.retry')}</button>
    </div>`;
    const readiness = this.readiness;
    if (!readiness?.examEligible) {
      if (!readiness || readiness.examToGo === undefined || readiness.examWordsNeeded === undefined) return html`<div class="card exam-readiness" role="status">${t('exam.readinessUnavailable')}</div>`;
      const key = readiness.examsTaken > 0 ? 'home.examLockedNext' : 'home.examLocked';
      const recovery = readiness.examRecovery;
      const action = recovery && 'action' in recovery ? recovery.action : undefined;
      const href = action === 'round' ? '/round' : action === 'capture' ? '/record#wanted' : undefined;
      return html`<div class="card exam-readiness" role="status">
        <div>${t(key, { needed: readiness.examWordsNeeded, n: readiness.examToGo })}</div>
        ${recovery ? html`<p class="muted small">${t(`home.examRecovery.${recovery.reason}`)}</p>` : nothing}
        ${href && action ? html`<a class="btn ghost small" href=${href} @click=${(e: Event) => { e.preventDefault(); go(href); }}>${t(`home.examRecovery.action.${action}`)}</a>` : nothing}
      </div>`;
    }
    return html`<div class="card">
      <div>${t('exam.intro')}</div>
      <button class="btn primary block ${this.loading ? 'preparing' : ''}" style="margin-top:14px;min-height:52px" @click=${this.start} ?disabled=${this.loading}>
        ${this.loading ? t('exam.writing') : t('exam.start')}
      </button>
    </div>`;
  }

  private async refreshReadiness(afterRefusal = false): Promise<LearnStatus | null> {
    const seq = ++this.readinessSeq;
    this.checkingReadiness = true;
    this.readinessUnavailable = false;
    this.refusedWithEligibleStatus = false;
    try {
      const current = await api.learnStatus();
      if (seq !== this.readinessSeq || !this.isConnected) return current;
      this.readiness = current;
      this.refusedWithEligibleStatus = afterRefusal && current.examEligible;
      return current;
    } catch {
      if (seq !== this.readinessSeq || !this.isConnected) return null;
      this.readiness = undefined;
      this.readinessUnavailable = true;
      return null;
    } finally {
      if (seq === this.readinessSeq && this.isConnected) this.checkingReadiness = false;
    }
  }

  /**
   * While the story is written (10–30 s): a warm page whose lines ink in one after another, a quill rocking at its
   * foot, what is being made under it and, after 20 s, one line saying a story can take half a minute. Shown only
   * after a moment; nothing on it claims progress.
   */
  private renderStoryWait() {
    const widths = storyWidths(STORY_LINES);
    const delays = inkDelays(widths.length, 1300, 11_000);
    return html`<div class="wait-late exam-wait" aria-busy="true" style="margin-top:10px">
      <div class="wait-paper" aria-hidden="true">
        ${widths.map((w, k) => html`<span class="wait-skel" style="width:${Math.round(w * 100)}%;--d:${delays[k]}ms${k % 4 === 3 ? ';margin-bottom:16px' : ''}"></span>`)}
        <span class="wait-quill">✒︎</span>
      </div>
      <wait-caption .lines=${[t('wait.exam.words'), t('wait.exam.story'), t('wait.exam.questions')]} long=${t('wait.exam.long')}></wait-caption>
    </div>`;
  }

  /** The card on screen and the moment its answer posts to: a question, or an easier card after them. */
  private current(): { task: ExamTask | PackTask; momentId: string } | undefined {
    if (this.phase === 'easier') {
      const e = this.easier[this.i];
      return e ? { task: e.task, momentId: e.momentId } : undefined;
    }
    const task = this.exam?.tasks[this.i];
    return task ? { task, momentId: task.momentId } : undefined;
  }

  /** The last card before the score. */
  private isLast(): boolean {
    if (this.phase === 'easier') return this.i + 1 >= this.easier.length;
    return this.i + 1 >= (this.exam?.tasks.length ?? 0) && !this.easier.length;
  }

  /** Catalan line to play aloud for listen missions (hearing only — never shown as text). */
  private audioText(): string | null {
    return listenAudioText(this.current()?.task.props as Record<string, unknown> | undefined);
  }

  private playAudio = () => {
    const task = this.current();
    const a = this.audioText();
    // Queue spans moments; speak() falls back to device speech if the server has no TTS.
    if (task && a) void speak(task.momentId, a);
  };

  /** Past exams (v1 ticket 10): date, score and the words to work on, as the server stored them. */
  private renderPast() {
    if (!this.past && !this.pastError) return nothing;
    const list = this.past ?? [];
    return html`
      <div class="card past-exams">
        <h2>${t('exam.pastTitle')}</h2>
        ${this.pastError
          ? html`<div class="muted small">${t('exam.pastError')}</div>`
          : !list.length
            ? html`<div class="muted small">${t('exam.pastNone')}</div>`
            : html`<ul class="past-exam-list" style="list-style:none;margin:0;padding:0">
                ${list.map((e) => {
                  const work = [...new Set(e.results.filter((r) => r.result !== 'right').map((r) => r.latin || r.ca || r.itemKey))];
                  return html`<li class="past-exam" data-exam=${e.id} style="padding:6px 0;border-top:1px solid var(--line, #333)">
                    <div>${t('exam.pastLine', { date: fmtWhen(e.finishedAt), score: e.score, total: e.total, pct: pctOf(e.score, e.total) })}</div>
                    ${work.length
                      ? html`<div class="muted small">${t('exam.pastWork')} ${targetList(work)}</div>`
                      : nothing}
                  </li>`;
                })}
              </ul>`}
      </div>
    `;
  }

  /** Load the stored scores once this exam's answers have landed. */
  private loadPast = async () => {
    await Promise.allSettled([...this.inflight]);
    try {
      this.past = (await api.pastExams()).exams;
      this.pastError = false;
    } catch (e) {
      console.warn('could not load past exams', e);
      this.pastError = true;
    }
  };

  private renderDone() {
    const n = this.exam!.tasks.length;
    // The stored score when the server has settled it (every answer saved), else this screen's own count.
    const stored = this.past?.find((e) => e.id === this.exam!.id);
    const correct = stored ? stored.score : this.correct;
    const pct = n ? pctOf(correct, n) : 0;
    const pass = pct >= 80;
    return html`
      <div class="card" style="text-align:center">
        <div style="font-size:32px">${pass ? '🎉' : '✓'}</div>
        <div><strong>${t('exam.score', { correct, n, pct })}</strong> — ${pass ? t('exam.passed') : t('exam.passMark')}</div>
        ${this.missed.length
          ? html`<div style="text-align:start;margin-top:8px">
              <div><strong>${t('lesson.worthLook')}</strong> <span class="muted small">${t('lesson.worthLookHint')}</span></div>
              ${this.missed.map(missedRow)}
            </div>`
          : nothing}
        <div class="muted small" style="margin-top:8px">${t('exam.missedNote')}</div>
        ${this.renderUnsaved()}
        <lesson-feedback scope=${`exam:${this.exam!.id}`}></lesson-feedback>
        <div style="display:flex;gap:8px;margin-top:14px">
          <button class="btn" style="flex:1" @click=${() => go('/')}>${t('common.home')}</button>
          <button class="btn primary ${this.loading ? 'preparing' : ''}" style="flex:1" @click=${this.start} ?disabled=${this.loading}>${t('exam.newExam')}</button>
        </div>
      </div>
    `;
  }

  private start = async () => {
    if (this.loading) return;
    if (this.readiness?.examEligible !== true) {
      await this.refreshReadiness();
      return;
    }
    this.loading = true;
    this.error = '';
    try {
      this.exam = await api.createExam();
      this.i = 0;
      this.correct = 0;
      this.missed = [];
      this.easier = [];
      this.answered = false;
      this.showMe = false;
      this.recordedFor = -1;
      this.phase = 'story';
    } catch (e) {
      if (e instanceof HttpError && e.code === 'exam-few-words') {
        this.error = '';
        await this.refreshReadiness(true);
      } else this.error = errorText(e);
    } finally {
      this.loading = false;
    }
  };

  private beginTest = () => {
    this.phase = 'test';
    this.renderTask();
  };

  private resetSurface() {
    this.processor?.processMessages([{ version: 'v0.9', deleteSurface: { surfaceId: SURFACE_ID } }]);
    this.processor?.processMessages([{ version: 'v0.9', createSurface: { surfaceId: SURFACE_ID, catalogId: CATALOG_ID } }]);
  }

  private renderTask() {
    const task = this.current()?.task;
    if (!task || !this.processor) return;
    const total = this.phase === 'easier' ? this.easier.length : this.exam!.tasks.length;
    this.resetSurface();
    try {
      this.processor.processMessages([{
        version: 'v0.9',
        updateComponents: {
          surfaceId: SURFACE_ID,
          components: [
            { id: 'root', component: 'Column', children: ['p', 'q'] },
            { id: 'p', component: 'Progress', done: this.i + 1, total },
            { id: 'q', component: COMPONENT[task.type], ...task.props },
          ],
        },
      }] as never);
    } catch (e) {
      console.error('exam render failed', e);
      this.error = t('exam.questionError', { n: this.i + 1 });
    }
  }

  /** Remember a miss for the recap; a later correct retry on the same card clears it. */
  private noteMiss(task: ExamTask, ctx: Record<string, unknown>, correct: boolean) {
    this.missed = noteMissIn(this.missed, this.i, task.props as Record<string, unknown>, task.itemKey, ctx, correct);
  }

  private onAction(action: A2uiClientAction) {
    const cur = this.current();
    // Hints are free and disputes are practice-only: neither scores in an exam.
    if (action.name === 'hint') { if (cur) this.exerciseEvent(cur, 'hint'); return; }
    if (action.name === 'dispute') return;
    const ctx = (action.context ?? {}) as Record<string, unknown>;
    if ((this.phase !== 'test' && this.phase !== 'easier') || !this.exam || !cur || this.i === this.recordedFor) {
      this.answered = true;
      if (cur && this.showMe && !answerHidden(cur.task.type, action.name, ctx)) this.answerSeen(cur, ctx);
      else if (cur && ctx.revealed === true) this.exerciseEvent(cur, 'reveal');
      else if (cur && (action.name === 'answer' || action.name === 'grade')) this.exerciseEvent(cur, 'retry');
      return;
    }
    this.answered = true;
    this.recordedFor = this.i;
    const { task, momentId } = cur;
    const correct = action.name === 'answer' ? ctx.correct === true : false;
    // Ticket 32: never leave a miss without its answer on screen.
    this.showMe = answerHidden(task.type, action.name, ctx);
    // Easier cards come after the questions and never change the score.
    if (this.phase === 'test') {
      if (correct) this.correct += 1;
      this.noteMiss(task as ExamTask, ctx, correct);
    }
    void this.record(momentId, {
      taskId: task.id,
      itemKey: task.itemKey,
      type: task.type,
      correct,
      revealed: ctx.revealed === true,
      production: task.production,
      productionKind: task.productionKind,
      // No assessed: exams have no machine-scored pronunciation tasks.
      hintsUsed: typeof ctx.hintsUsed === 'number' ? ctx.hintsUsed : undefined,
      given: givenText(task.type, ctx, correct),
    });
  };

  /**
   * The answer is on screen after a miss (ticket 32), so Next comes back. A
   * Show me is saved once, unscored, as a reveal (never touching the score);
   * a right retry or a scratch after the miss is not saved, as before.
   */
  private answerSeen(cur: { task: ExamTask | PackTask; momentId: string }, ctx: Record<string, unknown>) {
    this.showMe = false;
    if (ctx.revealed !== true) return;
    const { task, momentId } = cur;
    this.exerciseEvent(cur, 'reveal');
    void this.record(momentId, {
      taskId: task.id, itemKey: task.itemKey, type: task.type, correct: false, revealed: true,
      production: task.production, productionKind: task.productionKind,
      hintsUsed: typeof ctx.hintsUsed === 'number' ? ctx.hintsUsed : undefined,
    });
  }

  /** The bar's Show me: the card shows its answer, and its reveal comes back through onAction. */
  private showAnswer = () => {
    if (!showAnswerOn(this)) this.showMe = false;
  };

  private skip = () => {
    const cur = this.current();
    if (!cur || (this.phase !== 'test' && this.phase !== 'easier')) return;
    const { task, momentId } = cur;
    if (this.i !== this.recordedFor) {
      this.recordedFor = this.i;
      if (this.phase === 'test') this.noteMiss(task as ExamTask, {}, false);
      void this.record(momentId, {
        taskId: task.id, itemKey: task.itemKey, type: task.type,
        correct: false, skipped: true, skipReason: 'dont_know', production: task.production,
        productionKind: task.productionKind,
      });
    }
    this.advance();
  };

  private async record(momentId: string, a: AttemptInput) {
    // Name the exam, so the server finds the step it served this card at
    // (exam recalls and new-line gap-fills are step 3). No step is sent. An
    // easier card is a served review card, not one of the exam's.
    const examId = this.phase === 'easier' ? undefined : this.exam?.id;
    const currentTask = this.current()?.task;
    const task = currentTask?.id === a.taskId ? currentTask : this.exam?.tasks.find((candidate) => candidate.id === a.taskId);
    const u = { momentId, a: {
      ...a, ...(task?.exposureId ? { exposureId: task.exposureId } : {}),
      ...(!a.revealed && !a.skipped && !a.selfReport ? { interaction: 'first_attempt' as const } : {}),
      ...(examId ? { examId } : {}), attemptId: makeClientId(), ...cardMs(momentId, a.taskId),
    } };
    // A save during a Retry joins it, so the answers still land oldest first.
    if (this.retrying) {
      this.unsaved = [...this.unsaved, u];
      return;
    }
    this.saving = true;
    try {
      // A failed save is kept for Retry rather than lost silently; a refused one is said, not retried.
      const sending = this.send(u);
      this.inflight.add(sending);
      const r = await sending;
      this.inflight.delete(sending);
      if (r === 'retry') this.unsaved = [...this.unsaved, u];
      else if (r === 'refused') this.lost += 1;
    } finally {
      this.saving = false;
    }
  }

  /** Send one answer to the learner's record. */
  private async send(u: UnsavedAttempt): Promise<SendResult> {
    try {
      const r = await api.recordAttempt(u.momentId, u.a);
      if (u.a.exposureId) void api.exerciseEvent(u.momentId, u.a.exposureId, makeClientId(), 'browser_ack').catch(() => undefined);
      if (r.retreat) this.queueEasier(withoutSaying(r.retreat));
      return 'saved';
    } catch (e) {
      console.warn('could not record attempt', e);
      return isRetryable(e) ? 'retry' : 'refused';
    }
  }

  private exerciseEvent(cur: { task: PackTask; momentId: string }, kind: 'hint' | 'retry' | 'reveal' | 'browser_ack') {
    if (cur.task.exposureId) void api.exerciseEvent(cur.momentId, cur.task.exposureId, makeClientId(), kind).catch(() => undefined);
  }

  /**
   * A missed word's easier card (ticket 04): kept for after the questions, not
   * inside the story. After the score it leads the next review instead.
   */
  private queueEasier(e: ReviewQueueEntry) {
    if (this.phase !== 'test' && this.phase !== 'easier') return;
    if (this.easier.some((x) => x.item.key === e.item.key)) return;
    this.easier = [...this.easier, { ...e, retreat: true }];
  }

  /** Re-send the unsaved answers oldest first, same ids; stop at the first that still fails. */
  private retryUnsaved = async () => {
    if (this.retrying) return;
    this.retrying = true;
    while (this.unsaved.length) {
      const u = this.unsaved[0];
      const r = await this.send(u);
      if (r === 'retry') break;
      this.unsaved = this.unsaved.filter((x) => x !== u);
      if (r === 'refused') this.lost += 1;
    }
    this.retrying = false;
    // The last answers landed: the stored score can settle now.
    if (this.phase === 'done' && !this.unsaved.length) void this.loadPast();
  };

  /** Non-blocking: the exam goes on; Retry sends the same answers again, a refusal is only dismissed. */
  private renderUnsaved() {
    const n = this.unsaved.length;
    const lost = this.lost;
    if (!n && !lost) return nothing;
    return html`
      ${n
        ? html`<div class="note save-error" role="alert" style="margin-top:6px;display:flex;gap:8px;align-items:center">
            <span style="flex:1">${n === 1 ? t('lesson.saveFailed') : t('lesson.saveFailedMany', { n })}</span>
            <button class="btn small" @click=${this.retryUnsaved} ?disabled=${this.retrying || this.saving}>
              ${this.retrying ? t('lesson.saving') : t('common.retry')}
            </button>
          </div>`
        : nothing}
      ${lost
        ? html`<div class="note save-error" role="alert" style="margin-top:6px;display:flex;gap:8px;align-items:center">
            <span style="flex:1">${lost === 1 ? t('lesson.saveRefused') : t('lesson.saveRefusedMany', { n: lost })}</span>
            <button class="btn small" @click=${() => (this.lost = 0)}>${t('common.dismiss')}</button>
          </div>`
        : nothing}
    `;
  }

  private next = () => {
    if (!this.answered || this.showMe) return; // hard gate
    this.advance();
  };

  private advance() {
    if (!this.exam) return;
    if (this.phase === 'test' && this.i + 1 >= this.exam.tasks.length && this.easier.length) {
      this.phase = 'easier';
      this.i = 0;
      this.recordedFor = -1;
      this.answered = false;
      this.showMe = false;
      this.renderTask();
      return;
    }
    if (this.isLast()) {
      this.phase = 'done';
      void this.loadPast();
      return;
    }
    this.i += 1;
    this.answered = false;
    this.showMe = false;
    this.renderTask();
  }
}

function pctOf(score: number, total: number): number {
  return total ? Math.round((score / total) * 100) : 0;
}

/** A finished exam's date and time, in the interface language. */
function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleString(currentLang(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
